import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Avatar,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { FiArrowLeft, FiCalendar, FiCheck, FiClock, FiMail, FiPhone, FiUser } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { Tooltip } from '../components/Tip';
import { CalEmpty, CalPageHeader, CalSectionHeading, CalSurface, alpha, useCalTheme } from '../components/ui';
import { BookingShell, BookingSteps, DetailRow } from './BookingParts';
import {
  formatPhoneNumber,
  toInternationalFormat,
  isValidPhoneNumber,
  getPhoneValidationError,
  COUNTRY_CODES,
  type CountryCode,
} from '../../authentication';
import {
  PUBLIC_CALENDAR_CONFIRM_QUERY as PUBLIC_CALENDAR_QUERY,
  CREATE_PUBLIC_BOOKING,
} from '../queries';

// ==================== TYPES ====================

interface LocationState {
  eventTypeId: string;
  eventTypeName?: string;
  startTime: string;
  endTime: string;
  calendarId?: string;
  calendarName?: string;
}

// ==================== COMPONENT ====================

export const PublicBookingConfirmPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const state = location.state as LocationState;

  // Form state
  const [bookerName, setBookerName] = useState('');
  const [bookerEmail, setBookerEmail] = useState('');
  const [bookerPhone, setBookerPhone] = useState(''); // International format for backend
  const [localPhoneNumber, setLocalPhoneNumber] = useState(''); // Display format for user
  const [notes, setNotes] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
    COUNTRY_CODES.find(c => c.code === '+61') || COUNTRY_CODES[0]
  );
  const [phoneError, setPhoneError] = useState<string>('');

  const t = useCalTheme();

  // Fetch calendar data
  const { data: calendarData } = useQuery(PUBLIC_CALENDAR_QUERY, {
    variables: { slug },
    skip: !slug,
  });

  const calendar = calendarData?.publicCalendar;

  // Mutation
  const [createBooking, { loading: creating }] = useMutation(CREATE_PUBLIC_BOOKING, {
    onCompleted: (data) => {
      if (data.createPublicBooking.requiresPayment && data.createPublicBooking.stripeCheckoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = data.createPublicBooking.stripeCheckoutUrl;
      } else {
        // Show success message
        toast({
          title: 'Booking Confirmed!',
          description: 'Your appointment has been scheduled. Check your email for confirmation.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Navigate to success page
        navigate(`/book/${slug}/success`, {
          state: {
            booking: data.createPublicBooking.booking,
            bookingToken: data.createPublicBooking.bookingToken,
          },
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Booking Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // ==================== HANDLERS ====================

  const handlePhoneInput = (value: string) => {
    const formatted = formatPhoneNumber(value, selectedCountry.code);
    setLocalPhoneNumber(formatted);

    // Store the full international number for backend
    const international = toInternationalFormat(formatted, selectedCountry.code);
    setBookerPhone(international);

    // Clear error when user types
    if (phoneError) setPhoneError('');
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (country) {
      setSelectedCountry(country);
      // Reformat existing number for new country
      if (localPhoneNumber) {
        handlePhoneInput(localPhoneNumber);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!state?.eventTypeId || !state?.startTime) {
      toast({
        title: 'Missing Information',
        description: 'Please select a time slot first.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      navigate(`/book/${slug}`);
      return;
    }

    // Validate phone number if provided
    if (localPhoneNumber && !isValidPhoneNumber(localPhoneNumber, selectedCountry)) {
      const error = getPhoneValidationError(localPhoneNumber, selectedCountry);
      setPhoneError(error);
      toast({
        title: 'Invalid Phone Number',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await createBooking({
        variables: {
          input: {
            calendarSlug: slug,
            eventTypeId: state.eventTypeId,
            startTime: state.startTime,
            bookerName,
            bookerEmail,
            bookerPhone, // Send international format to backend
            bookerTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            answers: notes ? { notes } : {},
          },
        },
      });
    } catch (err) {
      // Error handled by onError callback
      console.error('Booking error:', err);
    }
  };

  // ==================== VALIDATION ====================

  if (!state?.eventTypeId || !state?.startTime) {
    return (
      <BookingShell>
        <CalEmpty
          icon={FiCalendar}
          title="Pick a time first"
          action={(
            <Tooltip label="Go to the booking page to choose what to book and when. Nothing is booked.">
              <Button size="lg" minH="48px" leftIcon={<Icon as={FiArrowLeft} />} onClick={() => navigate(`/book/${slug}`)}>
                Go to the booking page
              </Button>
            </Tooltip>
          )}
        >
          This step needs a time chosen on the booking page. If you refreshed or opened this link directly, please start again — it only takes a moment.
        </CalEmpty>
      </BookingShell>
    );
  }

  const fieldBg = t.colorMode === 'dark' ? 'whiteAlpha.50' : 'white';

  // ==================== RENDER ====================

  return (
    <BookingShell maxW="container.md">
      <Stack spacing={{ base: 4, md: 6 }}>
        <CalPageHeader
          icon={FiCalendar}
          eyebrow={calendar ? (calendar.bookingPageTitle || calendar.name) : 'Book a time'}
          title="Confirm your booking"
          subtitle="Add your details and press Confirm booking. That's the last step."
          actions={calendar?.bookingPageProfilePhoto ? (
            <Avatar size="lg" src={calendar.bookingPageProfilePhoto || calendar.bookingPageLogo} name={calendar.bookingPageTitle || calendar.name} />
          ) : undefined}
        />

        <BookingSteps done={2} />

        {/* What they chose */}
        <CalSurface borderWidth="2px" borderColor={t.primary} bg={alpha(t.primary, t.colorMode === 'dark' ? 0.12 : 0.05)}>
          <CalSectionHeading icon={FiCalendar} title="Your booking" why="Not booked yet — confirm below to book it." />
          <Stack spacing={3}>
            {state.eventTypeName && <DetailRow icon={FiCheck} label="What">{state.eventTypeName}</DetailRow>}
            <DetailRow icon={FiCalendar} label="Date">{format(parseISO(state.startTime), 'EEEE d MMMM yyyy')}</DetailRow>
            <DetailRow icon={FiClock} label="Time">
              {format(parseISO(state.startTime), 'h:mm a')} – {format(parseISO(state.endTime), 'h:mm a')}
            </DetailRow>
          </Stack>
        </CalSurface>

        {/* Their details */}
        <CalSurface>
          <CalSectionHeading icon={FiUser} title="Your details" why="Used to send you the confirmation. Fields marked * are needed." />
          <form onSubmit={handleSubmit}>
            <Stack spacing={5}>
              <FormControl isRequired>
                <FormLabel fontSize="md" color={t.text}>
                  <HStack as="span" spacing={2} display="inline-flex"><Icon as={FiUser} /><span>Full name</span></HStack>
                </FormLabel>
                <Input
                  type="text"
                  value={bookerName}
                  onChange={(e) => setBookerName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  autoComplete="name"
                  size="lg"
                  bg={fieldBg}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="md" color={t.text}>
                  <HStack as="span" spacing={2} display="inline-flex"><Icon as={FiMail} /><span>Email address</span></HStack>
                </FormLabel>
                <Input
                  type="email"
                  value={bookerEmail}
                  onChange={(e) => setBookerEmail(e.target.value)}
                  placeholder="e.g. jane@example.com"
                  autoComplete="email"
                  inputMode="email"
                  size="lg"
                  bg={fieldBg}
                />
                <FormHelperText fontSize="sm" color={t.faint}>Your confirmation is sent here.</FormHelperText>
              </FormControl>

              <FormControl isInvalid={!!phoneError}>
                <FormLabel fontSize="md" color={t.text}>
                  <HStack as="span" spacing={2} display="inline-flex"><Icon as={FiPhone} /><span>Phone number (optional)</span></HStack>
                </FormLabel>
                <Stack spacing={2}>
                  <Select
                    aria-label="Country"
                    value={selectedCountry.code}
                    onChange={handleCountryChange}
                    size="lg"
                    bg={fieldBg}
                  >
                    {COUNTRY_CODES.map((country) => (
                      <option key={country.code + country.country} value={country.code}>
                        {country.flag} {country.country} ({country.code})
                      </option>
                    ))}
                  </Select>
                  <InputGroup size="lg">
                    <InputLeftAddon>{selectedCountry.code}</InputLeftAddon>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      value={localPhoneNumber}
                      onChange={(e) => handlePhoneInput(e.target.value)}
                      placeholder={selectedCountry.placeholder}
                      bg={fieldBg}
                    />
                  </InputGroup>
                </Stack>
                {phoneError && <FormErrorMessage fontSize="sm">{phoneError}</FormErrorMessage>}
                <Text fontSize="sm" color={t.faint} mt={2}>
                  For text-message reminders about your booking.
                  {localPhoneNumber && <> We'll use <b>{bookerPhone}</b>.</>}
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="md" color={t.text}>Anything else? (optional)</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything you'd like us to know before we meet"
                  rows={4}
                  fontSize="md"
                  bg={fieldBg}
                />
              </FormControl>

              <Box pt={2}>
                <Tooltip label="Book this time. You'll get a confirmation email; paid bookings go to a secure payment page first.">
                  <Button
                    type="submit"
                    bgImage={t.accentGradient}
                    color="white"
                    _hover={{ opacity: 0.92 }}
                    _active={{ opacity: 0.85 }}
                    size="lg"
                    minH="56px"
                    fontSize="lg"
                    width="full"
                    leftIcon={<Icon as={FiCheck} />}
                    isLoading={creating}
                    loadingText="Booking…"
                  >
                    Confirm booking
                  </Button>
                </Tooltip>
              </Box>

              <Tooltip label="Go back to choose a different time. Nothing is booked.">
                <Box w="full">
                <Button
                  variant="ghost"
                  size="lg"
                  minH="48px"
                  width="full"
                  color={t.muted}
                  leftIcon={<Icon as={FiArrowLeft} />}
                  onClick={() => navigate(-1)}
                  isDisabled={creating}
                >
                  Go back
                </Button>
                </Box>
              </Tooltip>
            </Stack>
          </form>
        </CalSurface>
      </Stack>
    </BookingShell>
  );
};

export default PublicBookingConfirmPage;
